<template>
  <div>
    <popup-hint hint="shopify">
      <template v-slot:breadcrumbs>
        <breadcrumbs home="Shopify"/>
      </template>
      <template v-slot:top>
        The Shopify app will process all fulfilled Shopify orders as Shipments in PURTRAK,
        automatically keeping your inventory current.
      </template>
    </popup-hint>

    <v-card flat>
      <div v-if="init"  class="text-center mt-16">
        <v-progress-circular indeterminate/>
      </div>

      <v-tabs :vertical="!$vuetify.breakpoint.mobile" show-arrows v-else>

        <template v-if="permissions.includes('config.write')">
          <v-tab>Connection</v-tab>
          <v-tab-item>
            <apps-shopify-connection :shopify="shopify" @disconnect="shopify=null" @refresh="shopify=$event"/>
          </v-tab-item>
        </template>

        <template v-if="permissions.includes('inventory.write')">
          <v-tab :disabled="!shopify || !shopify.connection">Products</v-tab>
          <v-tab-item v-if="shopify && shopify.connection">
            <apps-shopify-products :shopify="shopify"/>
          </v-tab-item>
        </template>

        <template v-if="permissions.includes('inventory.write')">
          <v-tab :disabled="!shopify || !shopify.connection">Inventory</v-tab>
          <v-tab-item v-if="shopify && shopify.connection">
            <apps-shopify-inventory :shopify="shopify" @saveSettings="saveSettings"/>
          </v-tab-item>
        </template>

        <template v-if="permissions.includes('selling.write')">
          <v-tab :disabled="!shopify|| !shopify.connection">Orders</v-tab>
          <v-tab-item v-if="shopify  && shopify.connection">
            <apps-shopify-orders :shopify="shopify" @saveSettings="saveSettings"/>
          </v-tab-item>
        </template>

        <template v-if="permissions.includes('selling')">
          <v-tab :disabled="!shopify || !shopify.connection" @click="clearAlerts()">
            <v-badge color="red" dot :value="showAlert">
              Log
            </v-badge>
          </v-tab>
          <v-tab-item v-if="shopify && shopify.connection" eager>
            <apps-shopify-log ref="log"/>
          </v-tab-item>
        </template>

      </v-tabs>
    </v-card>
  </div>
</template>

<script>
import AppsShopifyConnection from '@/views/apps/shopify/AppsShopifyConnection'
import AppsShopifyProducts from '@/views/apps/shopify/AppsShopifyProducts'
import AppsShopifyInventory from '@/views/apps/shopify/AppsShopifyInventory'
import AppsShopifyOrders from '@/views/apps/shopify/AppsShopifyOrders'
import AppsShopifyLog from '@/views/apps/shopify/AppsShopifyLog'

export default {
  name: 'AppsShopify',
  components: {AppsShopifyConnection,AppsShopifyProducts,
               AppsShopifyInventory,AppsShopifyOrders,AppsShopifyLog},
  props: {
    alert: {type:Boolean, required:false, default:false}
  },
  data() {
    return {
      showAlert: this.alert,
      init: true,
      shopify: null
    }
  },
  computed: {
    permissions() {
      return this.$store.getters.company.permissions;
    },
  },
  async mounted() {
    if (this.$route.query.state) {
      const results = await this.$apiCall(`mutation AppsShopify($state:String!, $query: String!) {
                                              shopifyAuthorize(state:$state, query:$query) {
                                                shop,connection
                                                locations {id,name}
                                                products {id,allowsVariants,productName,variants {id,variantName,sku,duplicateSKU}}
                                                locationMap{siteId,siteName,locationId}
                                                settings {inventoryUpdateFrequency,orderProcessingFrequency,createDraftOrders,createDraftOrdersOnLowInventory,
                                                customerForOrders,customerForExportOrders,customerForPOS,shipmentItemId,giftCardItemId,customItemId,tipItemId,feesItemId,
                                                qbo,paymentMethodId,qboAccountRef,inBond}
                                              }
                                            }`,
                                          {state:localStorage.getItem('oauthState'),
                                            query:new URL(location.href).search.substring(1)},
                                            false, 'shopify');
      if (!results.error) {
        this.shopify = results.data.shopifyAuthorize;
        // Set up initial default location mappings
        for (const location of this.shopify.locationMap) {
           const results2 = await this.$apiCall(`mutation AppsShopify($input: ShopifyLocationMappingInput!) {
                                                  shopifySetLocation(input:$input) {locationId}
                                                }`,
                                            {input:{siteId:location.siteId,locationId:this.shopify.locations[0].id}}, false, 'shopify');
          if (!results2.error)
            location.locationId = this.shopify.locations[0].id;
        }
      }
      else
        this.$router.replace({query: {}});
    }

    else {
      const results = await this.$apiCall(`query AppsShopify {
                                              shopify {
                                                shop,connection
                                                locations {id,name}
                                                locationMap {siteId,siteName,locationId}
                                                settings {inventoryUpdateFrequency,orderProcessingFrequency,createDraftOrders,createDraftOrdersOnLowInventory,
                                                customerForOrders,customerForExportOrders,customerForPOS,shipmentItemId,giftCardItemId,customItemId,tipItemId,feesItemId,
                                                inBond,qbo,paymentMethodId,qboAccountRef}
                                              }
                                            }`,
                                          {}, false, 'shopify');
      if (!results.error) {
        this.shopify = results.data.shopify;
        this.shop = this.shopify?.shop;
      }
    }

    localStorage.removeItem('oauthState');
    this.init = false;
  },
  methods: {
    async saveSettings() {
      const results = await this.$apiCall(`mutation AppsShopify($input:ShopifySettingsInput!) {
                                                shopifyUpdateSettings(input:$input) {settings{inventoryUpdateFrequency,orderProcessingFrequency,
                                                createDraftOrders,createDraftOrdersOnLowInventory,customerForOrders,customerForExportOrders,customerForPOS,shipmentItemId,
                                                giftCardItemId,customItemId,tipItemId,feesItemId,
                                                qbo,paymentMethodId,inBond}}
                                              }`,
                                          {input:this.shopify.settings}, false, 'shopify');
      if (!results.error)
        Object.assign(this.shopify.settings, results.data.shopifyUpdateSettings.settings);
    },
    async clearAlerts() {
      if (!this.alert)
        return;

      await this.$apiCall(`mutation AppsShopify {setAlertForAddOn(name:"Shopify",on:false)}`, {});
      await this.$store.dispatch('checkTasks', true);
      this.showAlert = false;
    }
  }
}
</script>